/**
 * SectionPropertiesPanel - Config-driven properties panel for page builder sections
 *
 * This component reads settings from section definitions (settingsSchema)
 * and automatically renders the appropriate field components.
 *
 * Sections are different from layout components:
 * - Sections store values in section.content (not props)
 * - Sections use getSectionDefinition (not getComponentDefinition)
 * - Sections are page-level building blocks (header, paragraph, image, video, cta)
 *
 * Features:
 * - Compact Framer-style layout matching PropertiesPanel
 * - Auto-groups fields by category with simple section dividers
 * - Uses reusable field components from fields/
 * - Single source of truth - no duplicate field definitions
 * - Supports conditional field visibility via showWhen
 */

import React, { useMemo } from 'react';
import { Edit } from 'lucide-react';
import { getSectionDefinition } from '../componentDefinitions';
import { renderField, SectionDivider, SelectField, ResponsiveVisibilityField } from '../fields';

/**
 * Groups an array of items by a key
 * @param {Array} array - Array to group
 * @param {string} key - Key to group by
 * @returns {Object} Grouped object { [keyValue]: items[] }
 */
function groupBy(array, key) {
  return array.reduce((groups, item) => {
    const value = item[key] || 'Other';
    if (!groups[value]) {
      groups[value] = [];
    }
    groups[value].push(item);
    return groups;
  }, {});
}

/**
 * Default category order for consistent presentation
 */
const CATEGORY_ORDER = [
  'Content',
  'Typography',
  'Size',
  'Button Style',
  'Colors',
  'Background',
  'Style',
  'Section Size',
  'Border',
  'Spacing',
  'Advanced',
  'Other',
];

/**
 * Sort categories according to predefined order
 * @param {string[]} categories - Array of category names
 * @returns {string[]} Sorted array
 */
function sortCategories(categories) {
  return categories.sort((a, b) => {
    const indexA = CATEGORY_ORDER.indexOf(a);
    const indexB = CATEGORY_ORDER.indexOf(b);
    // If not in predefined order, put at the end
    const orderA = indexA === -1 ? CATEGORY_ORDER.length : indexA;
    const orderB = indexB === -1 ? CATEGORY_ORDER.length : indexB;
    return orderA - orderB;
  });
}

/**
 * Check if a field should be visible based on showWhen condition
 * @param {Object} field - Field definition
 * @param {Object} content - Current section content
 * @returns {boolean}
 */
function isFieldVisible(field, content) {
  if (!field.showWhen) return true;

  const { field: dependentField, equals, notEquals, isNotEmpty } = field.showWhen;
  const dependentValue = content[dependentField];

  if (equals !== undefined) {
    return dependentValue === equals;
  }
  if (notEquals !== undefined) {
    return dependentValue !== notEquals;
  }
  if (isNotEmpty) {
    return dependentValue !== undefined && dependentValue !== null && dependentValue !== '';
  }
  return true;
}

/**
 * SectionPropertiesPanel - Renders settings panel for a selected section
 *
 * @param {Object} props
 * @param {string} props.sectionType - Section type identifier (header, paragraph, image, video, cta)
 * @param {Object} props.content - Current section content values
 * @param {Function} props.onUpdate - Callback when a property changes: (key, value) => void
 * @param {string} [props.className] - Additional CSS classes for the container
 * @param {boolean} [props.showHeader=true] - Whether to show the header with section name
 * @param {string} [props.activeBreakpoint] - Current active breakpoint for responsive preview
 * @param {Function} [props.onBreakpointChange] - Callback when breakpoint changes
 */
export default function SectionPropertiesPanel({
  sectionType,
  content = {},
  onUpdate,
  className = '',
  showHeader = true,
  page,
  activeBreakpoint,
  onBreakpointChange,
}) {
  // Get section definition and settings schema
  const { definition, settingsSchema } = useMemo(() => {
    if (!sectionType) {
      return { definition: null, settingsSchema: [] };
    }

    const def = getSectionDefinition(sectionType);
    if (!def) {
      console.warn(`No section definition found for type: ${sectionType}`);
      return { definition: null, settingsSchema: [] };
    }

    return { definition: def, settingsSchema: def.settingsSchema || [] };
  }, [sectionType]);

  // Group fields by category (filtering happens at render time)
  const fieldsByCategory = useMemo(() => {
    if (!settingsSchema.length) return {};
    return groupBy(settingsSchema, 'category');
  }, [settingsSchema]);

  // Get sorted category names
  const sortedCategories = useMemo(() => {
    return sortCategories(Object.keys(fieldsByCategory));
  }, [fieldsByCategory]);

  // Handle field value changes
  const handleFieldChange = (key, value) => {
    if (onUpdate) {
      onUpdate(key, value);
    }
  };

  // Get the current value for a field
  const getFieldValue = (field) => {
    const value = content[field.key];
    // Return the value if defined, otherwise fall back to default
    if (value !== undefined && value !== null) {
      return value;
    }
    return field.default ?? definition?.defaultContent?.[field.key];
  };

  // No section type provided
  if (!sectionType) {
    return (
      <div className={`${className}`}>
        <div className="text-center py-8 text-gray-500">
          <div className="mb-3">
            <Edit className="w-8 h-8 mx-auto text-gray-300" />
          </div>
          <h3 className="text-sm font-medium text-gray-400 mb-1">No section selected</h3>
        </div>
      </div>
    );
  }

  // No definition found
  if (!definition) {
    return (
      <div className={`${className}`}>
        <div className="text-center py-8 text-gray-500">
          <div className="mb-3">
            <Edit className="w-8 h-8 mx-auto text-gray-300" />
          </div>
          <h3 className="text-sm font-medium text-gray-400 mb-1">Unknown section type</h3>
          <p className="text-xs">No settings available for "{sectionType}"</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {/* Compact Header */}
      {showHeader && (
        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
          <h3 className="text-xs font-semibold text-gray-700">
            {definition.label}
          </h3>
        </div>
      )}

      {/* Settings grouped by category */}
      <div className="px-3 py-2">
        {sortedCategories.map((category, index) => {
          const allFields = fieldsByCategory[category];
          const fields = allFields?.filter((field) => isFieldVisible(field, content));
          if (!fields || fields.length === 0) return null;

          return (
            <SectionDivider
              key={category}
              title={category}
              className={index === 0 ? 'pt-0 mt-0 border-t-0' : ''}
            >
              {fields.map((field) => {
                const value = getFieldValue(field);
                return renderField(field, value, (newValue) => {
                  handleFieldChange(field.key, newValue);
                }, { compact: true, activeBreakpoint, onBreakpointChange, page });
              })}
            </SectionDivider>
          );
        })}

        {/* Advanced section - always shown for all section types */}
        <SectionDivider title="Advanced">
          <SelectField
            field={{
              key: 'overflow',
              label: 'Overflow',
              options: [
                { value: 'visible', label: 'Visible (default)' },
                { value: 'hidden', label: 'Hidden' },
                { value: 'scroll', label: 'Scroll' },
                { value: 'auto', label: 'Auto' },
                { value: 'clip', label: 'Clip' },
              ],
            }}
            value={content.overflow || 'visible'}
            onChange={(newValue) => handleFieldChange('overflow', newValue)}
            compact
          />
          <ResponsiveVisibilityField
            field={{
              key: 'visibility',
              label: 'Visibility',
              default: { base: true, tablet: null, desktop: null },
            }}
            value={content.visibility || { base: true, tablet: null, desktop: null }}
            onChange={(newValue) => handleFieldChange('visibility', newValue)}
            compact
          />
        </SectionDivider>

        {sortedCategories.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            <p className="text-xs">No settings available for this section</p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * SectionPropertiesPanelFlat - Flat version without category grouping
 * Useful for embedding in SectionEditor modal
 */
export function SectionPropertiesPanelFlat({
  sectionType,
  content = {},
  onUpdate,
  className = '',
  activeBreakpoint,
  onBreakpointChange,
  page,
}) {
  const { definition, settingsSchema } = useMemo(() => {
    if (!sectionType) {
      return { definition: null, settingsSchema: [] };
    }

    const def = getSectionDefinition(sectionType);
    return {
      definition: def,
      settingsSchema: def?.settingsSchema || [],
    };
  }, [sectionType]);

  // Filter fields based on showWhen conditions
  const visibleSchema = useMemo(() => {
    return settingsSchema.filter((field) => isFieldVisible(field, content));
  }, [settingsSchema, content]);

  const handleFieldChange = (key, value) => {
    if (onUpdate) {
      onUpdate(key, value);
    }
  };

  const getFieldValue = (field) => {
    const value = content[field.key];
    if (value !== undefined && value !== null) {
      return value;
    }
    return field.default ?? definition?.defaultContent?.[field.key];
  };

  if (!sectionType || !definition) {
    return null;
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {visibleSchema.map((field) => {
        const value = getFieldValue(field);
        return renderField(field, value, (newValue) => {
          handleFieldChange(field.key, newValue);
        }, { compact: true, activeBreakpoint, onBreakpointChange, page });
      })}

      {/* Advanced controls - always included */}
      <SectionDivider title="Advanced">
        <SelectField
          field={{
            key: 'overflow',
            label: 'Overflow',
            options: [
              { value: 'visible', label: 'Visible (default)' },
              { value: 'hidden', label: 'Hidden' },
              { value: 'scroll', label: 'Scroll' },
              { value: 'auto', label: 'Auto' },
              { value: 'clip', label: 'Clip' },
            ],
          }}
          value={content.overflow || 'visible'}
          onChange={(newValue) => handleFieldChange('overflow', newValue)}
          compact
        />
        <ResponsiveVisibilityField
          field={{
            key: 'visibility',
            label: 'Visibility',
            default: { base: true, tablet: null, desktop: null },
          }}
          value={content.visibility || { base: true, tablet: null, desktop: null }}
          onChange={(newValue) => handleFieldChange('visibility', newValue)}
          compact
        />
      </SectionDivider>
    </div>
  );
}

/**
 * useSectionPropertiesPanel - Hook for managing section properties panel state
 *
 * @param {string} sectionType - The section type
 * @param {Object} content - Current section content
 * @param {Function} updateContent - Function to update section content: (key, value) => void
 * @returns {Object} { sectionType, content, onUpdate, definition, schema }
 */
export function useSectionPropertiesPanel(sectionType, content, updateContent) {
  const definition = sectionType ? getSectionDefinition(sectionType) : null;

  const onUpdate = (key, value) => {
    if (updateContent) {
      updateContent(key, value);
    }
  };

  return {
    sectionType,
    content,
    onUpdate,
    definition,
    schema: definition?.settingsSchema || [],
  };
}
